/**
 * Datos FICTICIOS para la vista previa. Ningún nombre, dirección o monto es real.
 * TODO(API): reemplazar este escenario por datos por empresa cuando existan
 * equipos/capacidad/descansos, cliente flexible, checklist, ubicación consentida,
 * métricas mensuales y preferencias. Las visitas y las sugerencias reales se leen
 * por separado en data/api.ts para no inventar equipos ni disponibilidad.
 */
import type {
  Block, Client, DateKey, Invoice, QueueItem, ServiceType, Team, Visit,
} from './types';
import { addDays } from '../lib/time';

export const TODAY: DateKey = '2026-10-29';
/** 10:40 am. */
export const NOW = 10 + 40 / 60;

const HQ = { lat: 30.2672, lng: -97.7431 };

export const TEAMS: Team[] = [
  { id: 'A', name: 'Equipo A', color: '#2F6FDE', workStart: 8, workEnd: 17, base: HQ,
    members: [
      { uid: 'rv', name: 'Rosa Vega', initials: 'RV', locationConsent: true },
      { uid: 'tg', name: 'Tomás Gil', initials: 'TG', locationConsent: true },
    ] },
  { id: 'B', name: 'Equipo B', color: '#8E44AD', workStart: 9.5, workEnd: 17.5, base: HQ,
    members: [
      { uid: 'at', name: 'Ana Torres', initials: 'AT', locationConsent: true },
      { uid: 'lm', name: 'Luis Méndez', initials: 'LM', locationConsent: true },
    ] },
  { id: 'C', name: 'Equipo C', color: '#0F8C7B', workStart: 8, workEnd: 17, base: HQ,
    members: [{ uid: 'cr', name: 'Carla Ruiz', initials: 'CR', locationConsent: true }] },
  { id: 'D', name: 'Equipo D', color: '#C2255C', workStart: 8, workEnd: 18, base: HQ,
    members: [
      { uid: 'dp', name: 'Diego Paz', initials: 'DP', locationConsent: true },
      { uid: 'em', name: 'Elena Mora', initials: 'EM', locationConsent: false },
    ] },
];

export const CLIENTS: Client[] = [
  { uid: 'ruiz', name: 'Ruiz', kind: 'cliente', address: '210 Lamar Blvd', location: { lat: 30.279, lng: -97.756 }, flexible: false },
  { uid: 'linares', name: 'Depto. Linares', kind: 'cliente', address: '1801 W 6th St', location: { lat: 30.296, lng: -97.770 }, flexible: false },
  { uid: 'mendez', name: 'Casa Méndez', kind: 'cliente', address: '4400 Duval St', location: { lat: 30.305, lng: -97.725 }, flexible: false },
  { uid: 'ortega', name: 'Casa Ortega', kind: 'cliente', address: '900 E 41st St', location: { lat: 30.262, lng: -97.705 }, flexible: false },
  { uid: 'rivera', name: 'Oficinas Rivera & Co.', kind: 'cliente', address: '7000 Burnet Rd', location: { lat: 30.240, lng: -97.690 }, flexible: false },
  { uid: 'saenz', name: 'Consultorio Sáenz', kind: 'cliente', address: '512 Elm St', location: { lat: 30.247, lng: -97.760 }, flexible: false },
  { uid: 'lugo', name: 'Casa Lugo', kind: 'cliente', address: '33 Oak Hill Dr', location: { lat: 30.236, lng: -97.748 }, flexible: true },
  { uid: 'gomez', name: 'Gómez', kind: 'cliente', address: '61 Riverside Dr', location: { lat: 30.243, lng: -97.778 }, flexible: false },
  { uid: 'torres', name: 'Torres Lofts', kind: 'cliente', address: '1200 S Congress', location: { lat: 30.250, lng: -97.749 }, flexible: false },
  { uid: 'vidal', name: 'Casa Vidal', kind: 'cliente', address: '808 Barton Springs', location: { lat: 30.262, lng: -97.733 }, flexible: true },
  { uid: 'paola', name: 'Paola Ruiz', kind: 'cliente', address: '1420 Oak St', location: { lat: 30.252, lng: -97.752 }, flexible: true, phone: '(512) 555-0148' },
];

const t = (h: number, m = 0) => h + m / 60;
const upd = '2026-10-29T09:12:41-06:00';

export const VISITS: Visit[] = [
  // Equipo A
  { uid: 'v-a1', date: TODAY, teamId: 'A', clientUid: 'ruiz', service: 'regular', start: 8, end: 9, status: 'completed', updatedAt: upd,
    assignments: [{ memberUid: 'rv', status: 'completed', startAt: 8, endAt: t(8, 50) }, { memberUid: 'tg', status: 'completed', startAt: 8, endAt: t(8, 50) }] },
  { uid: 'v-a2', date: TODAY, teamId: 'A', clientUid: 'linares', service: 'profunda', start: 9, end: 11.5, status: 'inProgress', updatedAt: upd,
    checklist: { done: 8, total: 14 },
    assignments: [{ memberUid: 'rv', status: 'inProgress', startAt: t(9, 4) }, { memberUid: 'tg', status: 'inProgress', startAt: t(9, 4) }] },
  { uid: 'v-a3', date: TODAY, teamId: 'A', clientUid: 'mendez', service: 'regular', start: 13, end: 15, status: 'scheduled', updatedAt: upd,
    assignments: [{ memberUid: 'rv', status: 'assigned' }, { memberUid: 'tg', status: 'assigned' }] },
  // Equipo B
  { uid: 'v-b1', date: TODAY, teamId: 'B', clientUid: 'ortega', service: 'regular', start: 9.75, end: 12.5, status: 'inProgress', updatedAt: upd,
    checklist: { done: 2, total: 14 },
    assignments: [{ memberUid: 'at', status: 'inProgress', startAt: t(9, 50) }, { memberUid: 'lm', status: 'declined' }] },
  { uid: 'v-b2', date: TODAY, teamId: 'B', clientUid: 'rivera', service: 'oficina', start: 13, end: 16, status: 'scheduled', updatedAt: upd,
    risk: { reason: 'Falta 1 persona', detail: 'Luis rechazó a las 9:12 · Ana va sola y lenta en Ortega' },
    assignments: [{ memberUid: 'at', status: 'assigned' }, { memberUid: 'lm', status: 'declined' }] },
  // Equipo C
  { uid: 'v-c1', date: TODAY, teamId: 'C', clientUid: 'saenz', service: 'oficina', start: 8, end: 11, status: 'inProgress', updatedAt: upd,
    checklist: { done: 13, total: 14 },
    assignments: [{ memberUid: 'cr', status: 'inProgress', startAt: t(8, 5) }] },
  { uid: 'v-c2', date: TODAY, teamId: 'C', clientUid: 'lugo', service: 'regular', start: 15, end: 17, status: 'scheduled', updatedAt: upd,
    assignments: [{ memberUid: 'cr', status: 'assigned' }] },
  // Equipo D
  { uid: 'v-d1', date: TODAY, teamId: 'D', clientUid: 'gomez', service: 'regular', start: 8, end: 9.75, status: 'completed', updatedAt: upd,
    assignments: [{ memberUid: 'dp', status: 'completed', startAt: 8, endAt: 9.7 }, { memberUid: 'em', status: 'completed' }] },
  { uid: 'v-d2', date: TODAY, teamId: 'D', clientUid: 'torres', service: 'mudanza', start: 11, end: 13, status: 'scheduled', updatedAt: upd,
    assignments: [{ memberUid: 'dp', status: 'assigned' }, { memberUid: 'em', status: 'assigned' }] },
  { uid: 'v-d3', date: TODAY, teamId: 'D', clientUid: 'vidal', service: 'regular', start: 14, end: 16, status: 'scheduled', updatedAt: upd,
    assignments: [{ memberUid: 'dp', status: 'assigned' }, { memberUid: 'em', status: 'assigned' }] },
  ...futureWeek(),
];

export const BLOCKS: Block[] = [
  { teamId: 'C', date: TODAY, start: 11, end: 12, label: 'Comida' },
  { teamId: 'A', date: TODAY, start: 11.5, end: 12, label: 'Traslado' },
  { teamId: 'D', date: TODAY, start: 13, end: 14, label: 'Comida' },
];

/** Agenda ficticia de los próximos 7 días (domingo sin servicio). */
function futureWeek(): Visit[] {
  // [teamId, start, end, service] por día relativo
  const plan: Record<number, [string, number, number, ServiceType][]> = {
    1: [['A', 8, 12, 'profunda'], ['A', 13, 17, 'regular'], ['B', 9.5, 13, 'oficina'], ['B', 13.5, 17.5, 'regular'], ['D', 8, 11, 'regular'], ['D', 12, 16, 'mudanza']],
    2: [['A', 8, 11, 'regular'], ['B', 9.5, 17.5, 'oficina'], ['C', 8, 12, 'profunda'], ['C', 13, 17, 'regular'], ['D', 8, 18, 'mudanza']],
    3: [],
    4: [['A', 8, 17, 'mudanza'], ['B', 13, 17.5, 'regular'], ['C', 8, 10, 'regular'], ['D', 8, 13, 'profunda'], ['D', 14, 18, 'regular']],
    5: [['A', 8, 12, 'regular'], ['A', 13, 16, 'regular'], ['B', 9.5, 17.5, 'profunda'], ['C', 8, 11, 'oficina'], ['C', 12, 15, 'regular'], ['D', 8, 12, 'regular'], ['D', 13, 15, 'regular']],
    6: [['A', 8, 11, 'regular'], ['A', 12, 14, 'regular'], ['B', 9.5, 12.5, 'regular'], ['C', 8, 17, 'mudanza'], ['D', 8, 16, 'oficina']],
    7: [['A', 8, 11, 'regular'], ['B', 9.5, 16.5, 'oficina'], ['C', 8, 11, 'regular'], ['C', 12, 14, 'regular'], ['D', 8, 18, 'mudanza']],
  };
  const out: Visit[] = [];
  const clients = ['ruiz', 'linares', 'mendez', 'ortega', 'saenz', 'gomez', 'torres', 'vidal'];
  let n = 0;
  for (const [day, rows] of Object.entries(plan)) {
    const date = addDays(TODAY, Number(day));
    for (const [teamId, start, end, service] of rows) {
      out.push({
        uid: `f-${date}-${teamId}-${start}`, date, teamId, clientUid: clients[n++ % clients.length],
        service, start, end, status: 'scheduled', updatedAt: upd, assignments: [],
      });
    }
  }
  return out;
}

/** Días sin servicio (domingo). */
export const CLOSED_DAYS: DateKey[] = ['2026-11-01'];

export const QUEUE: QueueItem[] = [
  { id: 'q1', channel: 'call', who: 'Paola Ruiz', kind: 'Cliente', text: 'Llamando · Flow Mobile · (512) 555-0148', when: 'ahora', action: 'Contestar', ringing: true, clientUid: 'paola' },
  { id: 'q2', channel: 'call', who: '(737) 555-0192', kind: 'Desconocido', text: 'Llamando · línea 2', when: 'ahora', action: 'Contestar', ringing: true },
  { id: 'q3', channel: 'sms', who: 'Marta Linares', kind: 'Cliente', text: '“¿Pueden venir el sábado en vez del viernes?”', when: '10:31', action: 'Reagendar' },
  { id: 'q4', channel: 'missed', who: '(512) 555-0190', kind: 'Desconocido', text: 'Llamada perdida', when: '9:58', action: 'Devolver' },
  { id: 'q5', channel: 'mail', who: 'Oficina Nova', kind: 'Prospecto', text: 'Solicitud de cotización · 3,200 sqft', when: '10:05', action: 'Crear estimado' },
  { id: 'q6', channel: 'social', who: '@lauren.homes', kind: 'Prospecto', text: 'Instagram · “¿Cuánto cobran por profunda?”', when: '9:48', action: 'Responder' },
  { id: 'q7', channel: 'web', who: 'Formulario web', kind: 'Prospecto', text: 'Mudanza · 2 recámaras · 8 nov', when: '9:12', action: 'Crear estimado' },
];

export const INVOICES: Invoice[] = [
  { uid: 'i1', clientUid: 'rivera', number: 'INV-2041', issuedOn: '2026-10-15', amount: 380, status: 'PENDING' },
  { uid: 'i2', clientUid: 'saenz', number: 'INV-2063', issuedOn: '2026-10-28', amount: 520, status: 'FAILED' },
  { uid: 'i3', clientUid: 'ortega', number: 'INV-2066', issuedOn: '2026-10-29', amount: 340, status: 'PENDING' },
  { uid: 'i4', clientUid: 'gomez', number: 'INV-2070', issuedOn: '2026-10-29', amount: 760, status: 'PAID' },
];

/** Trabajos por día de octubre: [hechos, programados, proyectados]. */
export const MONTH: [number, number, number][] = Array.from({ length: 31 }, (_, i) => {
  const d = i + 1;
  const wd = (d + 2) % 7; // 1 oct 2026 = jueves
  const base = wd === 6 ? 3 : wd === 5 ? 5 : 8;
  const tot = base + ((d * 7) % 4) - 1;
  if (d <= 28) return [tot, 0, 0];
  if (d === 29) return [5, 9, 0];
  return [0, Math.max(tot - 2, 2), 2];
});

export const MONTH_TOTAL = { done: 149, scheduled: 37, projected: 240 };

export const RHYTHM_BY_SERVICE: { service: ServiceType; label: string; estimateH: number; actualH: number }[] = [
  { service: 'regular', label: 'Limpieza regular', estimateH: 2, actualH: 2.1 },
  { service: 'profunda', label: 'Profunda', estimateH: 3, actualH: 3.52 },
  { service: 'oficina', label: 'Oficina', estimateH: 2.5, actualH: 2.33 },
  { service: 'mudanza', label: 'Mudanza', estimateH: 4, actualH: 4.17 },
];
